import { taskLabel } from "@/lib/task-reference";
import {
  taskInstructionLabel,
  taskInstructions,
} from "@/lib/task-instructions";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  type CarrierTripDetail,
  useCarrierTripWaypoint,
  useRemoveTripStop,
  useReorderTripStops,
  useSplitCarrierStop,
  useStartCarrierTripWaypoint,
} from "@/api/carrier";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { labelize, StatusPill } from "./dispatch-presenters";
import { canFailTask, type TaskFailureTarget } from "./task-failure";
import { ModalStopList } from "./modal-stops";
import { buildStops, type DerivedStop } from "./trip-stops";
import { isTripRoutingEditable } from "./trip-routing-state";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";

/**
 * The stops pane of the Trip focus dialog.
 *
 * Owns every stop mutation so success and failure report in one place, and
 * expands the selected stop's Tasks beneath its own row rather than in a
 * panel beside it — a stop's work belongs with the stop.
 */
export function TripStopsWorkflow({
  detail,
  armed,
  selectedWaypointId,
  onSelectWaypoint,
  onFailTask,
  reportSuccess,
  reportError,
}: {
  detail: CarrierTripDetail;
  /** Drag-to-reorder, armed from the focus toolbar. */
  armed: boolean;
  selectedWaypointId: string | null;
  onSelectWaypoint: (waypointId: string | null) => void;
  onFailTask?: (task: TaskFailureTarget) => void;
  reportSuccess: (message: string) => void;
  reportError: (error: unknown, fallback: string) => void;
}) {
  const reorderStops = useReorderTripStops();
  const removeStop = useRemoveTripStop();
  const startWaypoint = useStartCarrierTripWaypoint();
  const [removeWaypointId, setRemoveWaypointId] = useState<string | null>(null);
  const waypoint = useCarrierTripWaypoint(detail.id, selectedWaypointId);
  const stops = useMemo(() => buildStops(detail.waypoints), [detail.waypoints]);
  const editable = isTripRoutingEditable(detail.status);
  const pending =
    reorderStops.isPending || removeStop.isPending || startWaypoint.isPending;
  const taskTotal = useMemo(
    () => stops.reduce((total, stop) => total + stop.total, 0),
    [stops]
  );

  const handleReorder = async (reordered: DerivedStop[]) => {
    try {
      await reorderStops.mutateAsync({
        tripId: detail.id,
        waypointOrders: reordered.map((stop, stopIndex) => ({
          waypointId: stop.id,
          sequence: stopIndex + 1,
        })),
      });
      reportSuccess("Stop order updated.");
    } catch (error) {
      reportError(error, "Stop order could not be updated.");
      // Rethrow so the list puts the old order back. Reporting the failure but
      // leaving the new order on screen would show a sequence the Server
      // rejected, which a dispatcher would then plan a driver's day around.
      throw error;
    }
  };

  const handleRemoveStop = async () => {
    if (!removeWaypointId) return;
    try {
      await removeStop.mutateAsync({
        tripId: detail.id,
        waypointId: removeWaypointId,
      });
      if (selectedWaypointId === removeWaypointId) onSelectWaypoint(null);
      setRemoveWaypointId(null);
      reportSuccess("Stop removed from Trip.");
    } catch (error) {
      reportError(error, "Stop could not be removed.");
    }
  };

  const handleStartWaypoint = async (stop: DerivedStop) => {
    try {
      const result = await startWaypoint.mutateAsync({
        tripId: detail.id,
        waypointId: stop.id,
      });
      const summary = `${result.data.readyTaskIds.length} tasks ready; ${result.data.blockedTasks.length} blocked`;
      if (result.data.blockedTasks.length > 0) {
        const reasons = result.data.blockedTasks
          .map((blocked) => {
            const task = stop.tasks.find((item) => item.id === blocked.taskId);
            return `${task ? taskLabel(task) : "Task"}: ${blocked.message}`;
          })
          .join("; ");
        reportError(
          new Error(`${summary}. ${reasons}`),
          `${summary}. ${reasons}`
        );
      } else reportSuccess(summary);
    } catch (error) {
      reportError(error, `Stop ${stop.seq} could not be started.`);
    }
  };

  return (
    <div className="flex min-h-0 flex-col bg-white">
      <div className="flex items-center gap-2 border-b border-zinc-100 px-3.5 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
          {stops.length} {stops.length === 1 ? "stop" : "stops"} · {taskTotal}{" "}
          {taskTotal === 1 ? "task" : "tasks"}
        </span>
        {armed && (
          <span className="ml-auto text-[11px] font-semibold text-indigo-600">
            Drag to reorder
          </span>
        )}
      </div>
      {detail.status === "PLANNED" && !detail.plannedRoute && (
        <p
          role="status"
          className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          Route needs refresh.
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1.5">
        <ModalStopList
          stops={stops}
          color={detail.color ?? "#4f46e5"}
          selectedId={selectedWaypointId}
          armed={armed}
          busy={pending}
          canStart={detail.status === "ACTIVE"}
          canRemove={editable}
          onInspect={(stop) =>
            onSelectWaypoint(selectedWaypointId === stop.id ? null : stop.id)
          }
          onStart={handleStartWaypoint}
          onRemove={(stop) => setRemoveWaypointId(stop.id)}
          onReorder={handleReorder}
          renderExpanded={() => (
            <StopTasks
              loading={waypoint.isLoading}
              error={waypoint.error}
              data={waypoint.data}
              tripId={detail.id}
              tripStatus={detail.status}
              reportSuccess={reportSuccess}
              reportError={reportError}
              onFailTask={onFailTask}
            />
          )}
        />
      </div>

      <AlertDialog
        open={Boolean(removeWaypointId)}
        onOpenChange={(open) => !open && setRemoveWaypointId(null)}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove stop?</AlertDialogTitle>
            <AlertDialogDescription>
              Eligible Tasks will return to the unassigned queue. Completed work
              remains attached for history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeStop.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={removeStop.isPending}
              onClick={handleRemoveStop}
            >
              Remove stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The selected stop's Tasks, beneath its own row. */
function StopTasks({
  loading,
  error,
  data,
  tripId,
  tripStatus,
  reportSuccess,
  reportError,
  onFailTask,
}: {
  loading: boolean;
  error: unknown;
  data: ReturnType<typeof useCarrierTripWaypoint>["data"] | undefined;
  tripId: string;
  tripStatus: string;
  reportSuccess: (message: string) => void;
  reportError: (error: unknown, fallback: string) => void;
  onFailTask?: (task: TaskFailureTarget) => void;
}) {
  const split = useSplitCarrierStop();
  const [movedTaskId, setMovedTaskId] = useState("");
  const [sourceAnchorTaskId, setSourceAnchorTaskId] = useState("");
  const moved =
    data?.tasks.find((task) => task.id === movedTaskId) ?? data?.tasks[0];
  const remaining =
    data?.tasks.find(
      (task) => task.id === sourceAnchorTaskId && task.id !== moved?.id
    ) ?? data?.tasks.find((task) => task.id !== moved?.id);
  const pin = (task: typeof moved) =>
    task?.mapLocation
      ? `${task.mapLocation.lat},${task.mapLocation.lng}`
      : null;
  const canSplit =
    tripStatus === "PLANNED" &&
    (data?.tasks.length ?? 0) >= 2 &&
    data?.tasks.every((task) => task.status === "PENDING" && !task.startedAt) &&
    !data?.eta?.actualArrivalTime &&
    !data?.eta?.actualDepartureTime;
  return (
    <div className="mb-1.5 ml-8 space-y-1.5 border-l border-zinc-200 pl-3">
      {canSplit && data && (
        <details className="rounded-md border bg-white p-2 text-[11px]">
          <summary className="cursor-pointer font-semibold">
            Move Task to separate stop
          </summary>
          <div className="mt-2 space-y-2">
            <label htmlFor="split-task-to-move" className="block">
              Task to move
              <NativeSelect
                id="split-task-to-move"
                className="mt-1 w-full rounded border p-1"
                value={moved?.id ?? ""}
                onChange={(event) => {
                  setMovedTaskId(event.target.value);
                  setSourceAnchorTaskId("");
                }}
              >
                {data.tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    TSK-{task.taskNumber} · SHP-
                    {task.shipment?.shipmentNumber ?? "unknown"}
                  </option>
                ))}
              </NativeSelect>
            </label>
            <label htmlFor="split-remaining-pin" className="block">
              Remaining stop pin
              <NativeSelect
                id="split-remaining-pin"
                className="mt-1 w-full rounded border p-1"
                value={remaining?.id ?? ""}
                onChange={(event) => setSourceAnchorTaskId(event.target.value)}
              >
                {data.tasks
                  .filter((task) => task.id !== moved?.id && pin(task))
                  .map((task) => (
                    <option key={task.id} value={task.id}>
                      TSK-{task.taskNumber} · {pin(task)}
                    </option>
                  ))}
              </NativeSelect>
            </label>
            <p>Current pin: {data.locationKey}</p>
            <p>Remaining stop pin: {pin(remaining) ?? "Unavailable"}</p>
            <p>Separate stop pin: {pin(moved) ?? "Unavailable"}</p>
            <Button
              type="button"
              disabled={split.isPending || !moved || !remaining || !pin(moved)}
              onClick={async () => {
                if (!moved || !remaining || !data) return;
                try {
                  await split.mutateAsync({
                    tripId,
                    waypointId: data.id,
                    taskId: moved.id,
                    sourceTaskIds: data.tasks.map((task) => task.id),
                    sourceAnchorTaskId: remaining.id,
                    expectedSourcePin: data.locationKey,
                    expectedMovedPin: pin(moved) ?? "",
                    expectedRemainingPin: pin(remaining) ?? "",
                  });
                  reportSuccess(
                    "Task moved to a separate stop. Route refresh is pending."
                  );
                } catch (error) {
                  reportError(
                    error,
                    "Could not split stop. Review the Tasks and pins again."
                  );
                }
              }}
            >
              Move Task to separate stop
            </Button>
          </div>
        </details>
      )}
      {loading && (
        <div className="flex items-center gap-2 py-1 text-[11px] text-zinc-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading Tasks...
        </div>
      )}
      {Boolean(error) && (
        <p role="alert" className="py-1 text-[11px] text-red-700">
          Waypoint detail could not be loaded.
        </p>
      )}
      {data?.tasks.length === 0 && (
        <p className="py-1 text-[11px] text-zinc-500">No Tasks at this stop.</p>
      )}
      {data?.tasks.map((task) => (
        <div
          key={task.id}
          className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
            isTaskOnCancellationHold(task)
              ? "border border-zinc-200 bg-zinc-100 text-zinc-500"
              : "bg-zinc-50"
          }`}
        >
          <TaskReferenceDisplay task={task} className="flex-1" />
          <span className="min-w-0 flex-1 text-[11px] text-zinc-600">
            {labelize(task.type)} · SHP-
            {task.shipment?.shipmentNumber ?? "unknown"} ·{" "}
            {task.addressLine1 ?? "Unknown address"} ·{" "}
            {task.contactName ?? "No contact"}
            {task.contactPhone && ` · ${task.contactPhone}`}
            {task.addressLine2 && ` · Unit ${task.addressLine2}`}
            {taskInstructions(task) &&
              ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
            {task.mapLocation &&
              ` · Pin ${task.mapLocation.lat}, ${task.mapLocation.lng}`}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {isTaskOnCancellationHold(task) && (
              <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
                On hold · cancellation review
              </span>
            )}
            <StatusPill
              status={
                task.executionBlocked ? "NEEDS_INVESTIGATION" : task.status
              }
            />
            {onFailTask &&
              !isTaskOnCancellationHold(task) &&
              canFailTask(task.status, task.type) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[11px] text-red-700 hover:bg-red-50 hover:text-red-800"
                  aria-label={`Fail ${taskLabel(task)}`}
                  disabled={task.executionBlocked}
                  title={
                    task.executionBlocked
                      ? "Dispatch must resolve the loss investigation first"
                      : undefined
                  }
                  onClick={() => onFailTask(task)}
                >
                  Fail
                </Button>
              )}
          </span>
        </div>
      ))}
    </div>
  );
}
